/**
 * PhoneDialer.tsx
 *
 * Phone dialer app for making voice calls.
 * Phase 3: Skeleton with basic UI ✅
 * Phase 4: Full functionality with WebSocket integration ✅
 *
 * Features:
 * - Number pad (0-9, *, #, backspace)
 * - Call button with WebSocket integration
 * - Real-time call status (Dialing → Ringing → Connected → Ended)
 * - Live call timer (updates every second)
 * - End call button
 */

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Phone, PhoneOff, Delete, Mic, MicOff, Volume2, Bot, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import phoneWebSocketService from '@/services/phoneWebSocketService';
import type { PhoneCallStatusUpdate } from '@/services/phoneWebSocketService';

export interface PhoneDialerProps {
  onBack: () => void;
  disabled?: boolean;
}

/**
 * Number pad button. Supports optional long-press for the `0 → +` behaviour
 * every native dialer ships — hold 0 for ~500 ms and the long-press handler
 * fires INSTEAD of the plain onClick.
 */
interface NumberPadButtonProps {
  number: string;
  letters?: string;
  onClick: () => void;
  onLongPress?: () => void;
  disabled: boolean;
}

const NumberPadButton: React.FC<NumberPadButtonProps> = ({
  number,
  letters,
  onClick,
  onLongPress,
  disabled,
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const firedLongRef = useRef(false);

  const startPress = () => {
    if (!onLongPress || disabled) return;
    firedLongRef.current = false;
    timerRef.current = setTimeout(() => {
      firedLongRef.current = true;
      onLongPress();
    }, 500);
  };
  const endPress = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const handleClick = () => {
    // If the long-press handler already fired, swallow the click so we don't
    // append both `+` and `0`.
    if (firedLongRef.current) {
      firedLongRef.current = false;
      return;
    }
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerLeave={endPress}
      onPointerCancel={endPress}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center h-11 rounded-full',
        'bg-muted hover:bg-accent transition-colors',
        'active:scale-95 select-none',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span className="text-lg font-semibold text-foreground leading-none">{number}</span>
      {letters && <span className="text-[10px] text-muted-foreground leading-none mt-0.5">{letters}</span>}
    </button>
  );
};

/**
 * PhoneDialer Component (Full WebSocket Integration - Phase 4)
 */
export const PhoneDialer: React.FC<PhoneDialerProps> = ({ onBack, disabled = false }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [callStatus, setCallStatus] = useState<'idle' | 'dialing' | 'ringing' | 'connected' | 'ended' | 'failed'>('idle');
  const [callDuration, setCallDuration] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [muted, setMuted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.8);
  // Bootstrap from the service so a late mount doesn't miss the
  // synchronous 'connecting' emit that fires from setupAudioBridge before
  // this component's useEffect listener attaches. Without this, audioStatus
  // stays at 'idle' forever on first session-start (no 'connecting' pill,
  // dial button stuck disabled).
  const [audioStatus, setAudioStatus] = useState<'idle' | 'connecting' | 'flowing' | 'unavailable' | 'failed'>(
    () => phoneWebSocketService.getCurrentAudioStatus?.() ?? 'idle'
  );
  const [audioWarning, setAudioWarning] = useState<string>('');
  const [lastDtmfDigit, setLastDtmfDigit] = useState<string | null>(null);
  // Raw-PCM audio stream (bring-your-own AI agent). Populated when the user
  // taps "AI stream" during an active call; cleared when the call ends.
  const [streamInfo, setStreamInfo] = useState<{ wsUrl: string; token: string } | null>(null);
  const [streamLoading, setStreamLoading] = useState<boolean>(false);
  const [streamError, setStreamError] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const numberInputRef = useRef<HTMLInputElement | null>(null);
  // Pending reset-to-idle timeouts queued from a previous call's 'ended' /
  // 'failed' status. If the user starts a new call before they fire, the
  // dangling timeout would yank the new call's UI back to the dialer screen.
  // Cleared whenever a non-terminal status arrives or a new call starts.
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus the number input as soon as we're idle so a physical keyboard can
  // start typing without requiring a tap first. Re-focus whenever we return
  // to idle (after a call ends or fails) so the next call is one keystroke
  // away. We do NOT focus on mobile — `inputMode="tel"` would pop the system
  // keypad on top of the dialer, which conflicts with the on-screen keypad.
  useEffect(() => {
    if (callStatus !== 'idle') return;
    const isCoarsePointer =
      typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    if (isCoarsePointer) return;
    const id = window.setTimeout(() => numberInputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [callStatus]);

  // Register the hidden <audio> element with the service so received remote
  // tracks have a sink. Cleared on unmount so a remount doesn't leak the ref.
  useEffect(() => {
    if (audioRef.current) {
      phoneWebSocketService.setAudioElement(audioRef.current);
      audioRef.current.volume = volume;
    }
    return () => {
      phoneWebSocketService.setAudioElement(null);
    };
    // Intentionally only on mount — volume changes are pushed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push volume changes onto the audio element.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Listen for audio bridge status updates (separate event so we can show
  // a "no audio on this modem" notice without disturbing the call status).
  useEffect(() => {
    const handleAudioStatus = (data: any) => {
      const status = data?.status as 'connecting' | 'flowing' | 'unavailable' | 'failed' | undefined;
      if (status) setAudioStatus(status);
      if (data?.error) setAudioWarning(String(data.error));
      else if (status === 'flowing') setAudioWarning('');
    };
    phoneWebSocketService.on('phone_audio_status', handleAudioStatus);
    // LAZY VOICE: set up the audio bridge now that the Phone app is open. The
    // bridge is no longer started eagerly on session connect — only here, so a
    // browser-only session never pays the EG25 25s voice probe / mic prompt.
    // Idempotent: re-opening the dialer won't re-probe.
    phoneWebSocketService.ensureAudioBridge();
    // Re-sync to current service state in case setupAudioBridge already emitted
    // 'connecting' before this listener attached.
    const current = phoneWebSocketService.getCurrentAudioStatus?.();
    if (current && current !== 'idle') setAudioStatus(current);
    return () => phoneWebSocketService.off('phone_audio_status', handleAudioStatus);
  }, []);

  // Listen for call status updates from WebSocket
  useEffect(() => {
    const handleCallStatus = (data: PhoneCallStatusUpdate) => {
      console.log('[PhoneDialer] Received call status:', data);

      // Any non-terminal status (dialing/ringing/connected) means a new call
      // is in flight — cancel any pending reset-to-idle from a prior call's
      // 'ended'/'failed', otherwise it would yank this call back to the
      // dialer mid-flow. Also clear stale errorMessage so the previous
      // call's failure (e.g. "Edge did not respond") doesn't bleed back
      // into the idle-state dialer view after this new call ends.
      if (data.status === 'dialing' || data.status === 'ringing' || data.status === 'connected') {
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
          resetTimeoutRef.current = null;
        }
        setErrorMessage('');
      }

      setCallStatus(data.status);

      if (data.status === 'connected' && data.duration !== undefined) {
        setCallDuration(data.duration);
      }

      if (data.status === 'ended' && data.duration !== undefined) {
        setCallDuration(data.duration);
        setMuted(false);
        // NOTE: do NOT reset audioStatus here. The bridge persists across
        // multiple calls within a TLAM session (per the
        // setupAudioBridge-on-connect design); resetting to 'idle' would
        // disable the dial button after every call until a new 'flowing'
        // event arrives — which doesn't happen because the same pc/track
        // is reused. The service's emitAudioStatus is the single source
        // of truth; we only mirror its events.
        setAudioWarning('');
        setLastDtmfDigit(null);
        if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => {
          resetTimeoutRef.current = null;
          setCallStatus('idle');
          setCallDuration(0);
        }, 2000); // Show "Call ended" for 2 seconds
      }

      if (data.status === 'failed') {
        setErrorMessage(data.error || 'Call failed');
        // Same reasoning as the 'ended' branch — bridge persists across
        // calls; don't yank audioStatus to 'idle' or the next dial attempt
        // is blocked until a new 'flowing' arrives (which won't, since the
        // bridge wasn't torn down).
        setAudioWarning('');
        if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => {
          resetTimeoutRef.current = null;
          setCallStatus('idle');
          setErrorMessage('');
        }, 3000);
      }
    };

    phoneWebSocketService.on('phone_call_status', handleCallStatus);

    return () => {
      phoneWebSocketService.off('phone_call_status', handleCallStatus);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, []);

  // Call timer - increments every second while connected
  useEffect(() => {
    if (callStatus === 'connected') {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [callStatus]);

  const handleNumberClick = (num: string) => {
    if (callStatus === 'idle') {
      setPhoneNumber(prev => prev + num);
    }
  };

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const handleCall = () => {
    if (phoneNumber && callStatus === 'idle' && !disabled) {
      try {
        // Cancel any pending reset-to-idle from a previous call's terminal
        // status — the user is starting a new call before the 2 s "Call
        // ended" banner expired, so the old setTimeout would otherwise fire
        // mid-call and yank this dialer back to the keypad.
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
          resetTimeoutRef.current = null;
        }
        // Initiate call via WebSocket
        phoneWebSocketService.initiateCall(phoneNumber, 'voice');
        setCallStatus('dialing');
        console.log('[PhoneDialer] Initiated call to:', phoneNumber);
      } catch (error) {
        console.error('[PhoneDialer] Error initiating call:', error);
        setErrorMessage('Failed to initiate call');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    }
  };

  const handleEndCall = () => {
    if (disabled) return;
    // Flip UI immediately. The service also emits 'ended' synchronously, but
    // setting state here as well guarantees the button-press feels responsive
    // even if some other code path is mid-render.
    setCallStatus('ended');
    setTimeout(() => {
      setCallStatus('idle');
      setCallDuration(0);
      setMuted(false);
      setLastDtmfDigit(null);
    }, 1500);
    try {
      phoneWebSocketService.endCall();
    } catch (error) {
      console.error('[PhoneDialer] Error ending call:', error);
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    phoneWebSocketService.setMuted(next);
  };

  const handleDtmf = async (digit: string) => {
    if (callStatus !== 'connected') return;
    setLastDtmfDigit(digit);
    setTimeout(() => setLastDtmfDigit(null), 250);
    try {
      await phoneWebSocketService.sendDtmf(digit);
    } catch (error: any) {
      console.warn('[PhoneDialer] DTMF send failed:', error?.message || error);
    }
  };

  const handleGetStreamUrl = async () => {
    setStreamLoading(true);
    setStreamError('');
    try {
      const info = await phoneWebSocketService.startAudioStream('s16le');
      setStreamInfo({ wsUrl: info.wsUrl, token: info.token });
    } catch (error: any) {
      setStreamError(error?.message || 'Failed to start audio stream');
    } finally {
      setStreamLoading(false);
    }
  };

  const copyStreamUrl = async () => {
    if (!streamInfo) return;
    try {
      await navigator.clipboard.writeText(streamInfo.wsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Drop the stream panel whenever the call leaves the connected state.
  useEffect(() => {
    if (callStatus !== 'connected') {
      setStreamInfo(null);
      setStreamError('');
    }
  }, [callStatus]);

  const numberPadLayout = [
    [{ num: '1', letters: '' }, { num: '2', letters: 'ABC' }, { num: '3', letters: 'DEF' }],
    [{ num: '4', letters: 'GHI' }, { num: '5', letters: 'JKL' }, { num: '6', letters: 'MNO' }],
    [{ num: '7', letters: 'PQRS' }, { num: '8', letters: 'TUV' }, { num: '9', letters: 'WXYZ' }],
    [{ num: '*', letters: '' }, { num: '0', letters: '+' }, { num: '#', letters: '' }]
  ];

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Hidden audio sink for the WebRTC remote track. autoPlay so playback
          starts as soon as ontrack fires, but we still call .play() defensively
          in setAudioElement() because some browsers require a user gesture. */}
      <audio ref={audioRef} autoPlay playsInline className="hidden" />

      {/* Header */}
      <div className="flex items-center p-4 border-b border-border">
        <button
          onClick={onBack}
          className="p-2 hover:bg-muted rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <h2 className="ml-4 text-lg font-semibold text-foreground">Phone</h2>
      </div>

      {/* Phone number display / Call status */}
      <div className="flex flex-col items-center justify-center px-4 py-3 min-h-[80px] sm:min-h-[120px] sm:p-6">
        {callStatus === 'idle' ? (
          <>
            {/* Real input — supports paste, system-keyboard typing, cursor
                position. The dialpad is an alternative input method, not the
                only one. type="tel" gives the digit keypad on mobile; the
                onChange filter keeps only `+`, digits, `*`, `#`. */}
            <div className="flex items-center gap-2 w-full max-w-xs">
              <input
                ref={numberInputRef}
                type="tel"
                inputMode="tel"
                autoComplete="off"
                autoFocus
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d+*#]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && phoneNumber && !disabled) {
                    e.preventDefault();
                    handleCall();
                  }
                }}
                placeholder="Enter number"
                disabled={disabled}
                className={cn(
                  'flex-1 text-xl font-semibold text-center bg-transparent',
                  'border-none outline-none placeholder:text-muted-foreground/60',
                  'text-foreground min-w-0',
                )}
              />
              <button
                onClick={handleBackspace}
                disabled={!phoneNumber || disabled}
                aria-label="Backspace"
                className={cn(
                  'p-2 rounded-full hover:bg-muted transition-colors shrink-0',
                  (!phoneNumber || disabled) && 'opacity-30 cursor-not-allowed',
                )}
              >
                <Delete className="w-6 h-6 text-foreground" />
              </button>
            </div>
            {errorMessage && (
              <div className="mt-2 text-sm text-status-error">
                {errorMessage}
              </div>
            )}
            {/* Audio bridge status — visible on the dialer-input screen too
                (not just during a call) so the user can tell at a glance
                whether the dial button is greyed out because the bridge is
                still spinning up vs. because the bridge failed. Without
                this indicator the disabled dial button has no explanation
                and the user has to wait or hit Enter to discover the call
                will be silent. */}
            {audioStatus === 'connecting' && (
              <div className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5 justify-center w-full">
                <span className="w-1.5 h-1.5 rounded-full bg-status-warning animate-pulse" />
                Audio connecting…
              </div>
            )}
            {audioStatus === 'flowing' && (
              <div className="mt-2 text-xs text-status-success inline-flex items-center gap-1.5 justify-center w-full">
                <span className="w-1.5 h-1.5 rounded-full bg-status-success" />
                Audio live — ready to dial
              </div>
            )}
            {audioStatus === 'unavailable' && (
              <div className="mt-2 text-xs text-status-warning inline-flex items-center gap-1.5 justify-center w-full">
                <span className="w-1.5 h-1.5 rounded-full bg-status-warning" />
                No audio on this modem — signaling only
              </div>
            )}
            {audioStatus === 'failed' && (
              <div className="mt-2 text-xs text-status-error inline-flex items-center gap-1.5 justify-center w-full">
                <span className="w-1.5 h-1.5 rounded-full bg-status-error" />
                {audioWarning || 'Audio bridge failed'}
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            <div className="text-xl font-semibold text-foreground mb-2">{phoneNumber}</div>
            <div className="flex flex-col items-center gap-2">
              {callStatus === 'dialing' && (
                <span className="text-sm text-muted-foreground">Dialing...</span>
              )}
              {callStatus === 'ringing' && (
                <span className="text-sm text-muted-foreground">Ringing...</span>
              )}
              {callStatus === 'connected' && (
                <>
                  <span className="text-sm text-status-success">Connected</span>
                  <span className="text-sm text-muted-foreground">
                    {Math.floor(callDuration / 60)}:{(callDuration % 60).toString().padStart(2, '0')}
                  </span>
                </>
              )}
              {callStatus === 'ended' && (
                <span className="text-sm text-muted-foreground">Call ended</span>
              )}
              {callStatus === 'failed' && (
                <span className="text-sm text-status-error">{errorMessage || 'Call failed'}</span>
              )}
              {/* Audio status pill — visible from dial through end of call so
                  the user sees the bridge coming up alongside the call rather
                  than wondering whether the silence on connect is normal.
                  Hidden in 'idle' (no call in flight) and on 'failed' calls
                  (the call_status error already explains the failure). */}
              {callStatus !== 'failed' && audioStatus === 'connecting' && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-status-warning animate-pulse" />
                  Audio connecting…
                </span>
              )}
              {callStatus !== 'failed' && audioStatus === 'flowing' && (
                <span className="text-xs text-status-success inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-status-success" />
                  Audio live
                </span>
              )}
              {callStatus === 'connected' && audioStatus === 'unavailable' && (
                <span className="text-xs text-status-warning">
                  No audio on this modem — signaling only
                </span>
              )}
              {(callStatus === 'connected' || callStatus === 'dialing' || callStatus === 'ringing') && audioStatus === 'failed' && (
                <span className="text-xs text-status-error">
                  {audioWarning || 'Audio bridge failed'}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* In-call layout. When the audio bridge is OFF (iteration-2
          signaling-only mode), the DTMF keypad and mute/volume controls
          are grayed out — DTMF goes through the audio bearer so disabling
          audio means disabling DTMF too. End-Call stays prominent.

          NOTE: no `sm:` modifiers in this block. PhoneDialer is rendered
          inside the 390 px Pixel-7 phone frame, but Tailwind `sm:` keys off
          viewport width (≥640 px), not container width — so on a desktop
          viewport the keypad would inflate to h-16 + gap-4 + p-6 and push
          the End-Call button below the dialog's visible area. */}
      {callStatus === 'connected' && (() => {
        const audioBridgeOff = audioStatus === 'unavailable' || audioStatus === 'idle' || audioStatus === 'failed';
        const dialpadDisabled = disabled || audioBridgeOff;
        return (
        <div className="flex-1 flex flex-col justify-between px-4 py-3 min-h-0">
          {/* DTMF keypad */}
          <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto w-full">
            {numberPadLayout.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                {row.map((btn) => (
                  <button
                    key={btn.num}
                    onClick={() => handleDtmf(btn.num)}
                    disabled={dialpadDisabled}
                    className={cn(
                      'flex flex-col items-center justify-center h-11 rounded-full',
                      'bg-muted hover:bg-accent transition-colors',
                      'active:scale-95',
                      lastDtmfDigit === btn.num && 'bg-accent ring-2 ring-primary',
                      dialpadDisabled && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <span className="text-lg font-semibold text-foreground leading-none">{btn.num}</span>
                    {btn.letters && <span className="text-[10px] text-muted-foreground leading-none mt-0.5">{btn.letters}</span>}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </div>

          {/* AI stream (bring-your-own agent): hand a raw-PCM WebSocket URL +
              short-lived token to an external speech agent that can listen,
              talk, and drive the call. The browser's own audio is unaffected
              (the Pi fans capture out to both). */}
          <div className="mt-3 max-w-xs mx-auto w-full">
            <button
              data-testid="tlam-get-stream-url"
              onClick={handleGetStreamUrl}
              disabled={disabled || streamLoading}
              className={cn(
                'flex items-center justify-center gap-2 w-full h-9 rounded-lg',
                'bg-muted hover:bg-accent transition-colors text-sm text-foreground',
                (disabled || streamLoading) && 'opacity-40 cursor-not-allowed',
              )}
            >
              <Bot className="w-4 h-4" />
              {streamLoading ? 'Starting stream…' : streamInfo ? 'Refresh AI stream URL' : 'Get AI stream URL'}
            </button>
            {streamError && (
              <p data-testid="tlam-stream-error" className="mt-1 text-[11px] text-status-error">{streamError}</p>
            )}
            {streamInfo && (
              <div data-testid="tlam-stream-panel" className="mt-2 rounded-lg border border-border bg-card p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Agent WS URL</span>
                  <button
                    data-testid="tlam-copy-stream-url"
                    onClick={copyStreamUrl}
                    className="flex items-center gap-1 text-[10px] text-primary hover:underline"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p data-testid="tlam-stream-ws-url" className="mt-1 break-all text-[10px] text-foreground font-mono leading-snug">
                  {streamInfo.wsUrl}
                </p>
              </div>
            )}
          </div>

          {/* Bottom row: mute | vertical volume | End-Call. The End-Call
              button lives here (not in the always-on bottom button row)
              so the keypad layout reserves room for it. */}
          <div className="mt-3 flex items-end justify-between max-w-xs mx-auto w-full">
            <button
              onClick={toggleMute}
              disabled={dialpadDisabled}
              className={cn(
                'p-2.5 rounded-full transition-colors shrink-0',
                muted ? 'bg-status-error-bg text-status-error' : 'bg-muted hover:bg-accent text-foreground',
                dialpadDisabled && 'opacity-40 cursor-not-allowed'
              )}
              aria-label={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>

            <div className="flex flex-col items-center gap-1 px-2">
              <Volume2 className="w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                disabled={disabled || audioStatus !== 'flowing'}
                aria-label="Speaker volume"
                aria-orientation="vertical"
                className="cursor-pointer"
                style={{
                  writingMode: 'vertical-lr' as any,
                  direction: 'rtl',
                  WebkitAppearance: 'slider-vertical' as any,
                  width: '1.25rem',
                  height: '2.5rem',
                }}
              />
            </div>

            <button
              onClick={handleEndCall}
              disabled={disabled}
              aria-label="End call"
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center shrink-0',
                'bg-red-500 hover:bg-red-600 text-white shadow-lg',
                'transition-all active:scale-95',
                disabled && 'opacity-40 cursor-not-allowed hover:bg-red-500',
              )}
            >
              <PhoneOff className="w-5 h-5" />
            </button>
          </div>
        </div>
        );
      })()}

      {/* Number pad */}
      {callStatus === 'idle' && (
        <div className="flex-1 flex flex-col justify-center px-4 py-3">
          <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto w-full">
            {numberPadLayout.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                {row.map((btn) => (
                  <NumberPadButton
                    key={btn.num}
                    number={btn.num}
                    letters={btn.letters}
                    onClick={() => handleNumberClick(btn.num)}
                    onLongPress={btn.num === '0' ? () => handleNumberClick('+') : undefined}
                    disabled={disabled}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Call / End Call action — centered circular phone-style button.
          Green to dial when idle, red to hang up while dialing/ringing/
          ended. Hidden during 'connected' because the in-call section
          renders its own End-Call inline next to the volume slider — that
          way the disconnect is always visible inside the keypad layout
          even on short viewports. */}
      {callStatus !== 'connected' && (
        <div className="flex justify-center px-4 py-3">
          {callStatus === 'idle' ? (
            (() => {
              // Disable dial until the audio bridge is flowing — TLAM
              // session-start triggers a USB rebind on the modem (~15 s) so
              // ATD into a half-rebound modem times out. Audio bridge waits
              // for UAC and reports 'flowing' once everything is ready;
              // until then the dial button is greyed out with a hint.
              const audioReady = audioStatus === 'flowing'
                || audioStatus === 'unavailable'  // signaling-only mode also OK
                || audioStatus === 'failed';      // surface failure but allow attempt
              const dialDisabled = !phoneNumber || disabled || !audioReady;
              return (
                <button
                  onClick={handleCall}
                  disabled={dialDisabled}
                  aria-label={audioReady ? 'Call' : 'Audio connecting — please wait'}
                  title={audioReady ? undefined : 'Audio connecting — please wait'}
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center',
                    'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg',
                    'transition-all active:scale-95',
                    dialDisabled && 'opacity-40 cursor-not-allowed hover:bg-emerald-500',
                  )}
                >
                  <Phone className="w-5 h-5" />
                </button>
              );
            })()
          ) : (
            <button
              onClick={handleEndCall}
              disabled={disabled}
              aria-label="End call"
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center',
                'bg-red-500 hover:bg-red-600 text-white shadow-lg',
                'transition-all active:scale-95',
                disabled && 'opacity-40 cursor-not-allowed hover:bg-red-500',
              )}
            >
              <PhoneOff className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PhoneDialer;
